import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { format, parse, isValid } from "date-fns";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DateInputPickerProps {
  date?: Date;
  onDateChange: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
}

/**
 * Format input with dd/mm/yyyy mask
 */
function formatDateInput(value: string): string {
  // Remove all non-numeric characters
  const numbers = value.replace(/\D/g, '');
  
  // Apply mask: dd/mm/yyyy
  let formatted = '';
  for (let i = 0; i < numbers.length && i < 8; i++) {
    if (i === 2 || i === 4) {
      formatted += '/';
    }
    formatted += numbers[i];
  }
  
  return formatted;
}

export function DateInputPicker({ 
  date, 
  onDateChange, 
  placeholder = "DD/MM/YYYY",
  disabled = false,
  readOnly = false,
  className
}: Readonly<DateInputPickerProps>): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");

  // Update input value when date prop changes
  React.useEffect(() => {
    if (date && isValid(date)) {
      setInputValue(format(date, "dd/MM/yyyy"));
    } else {
      setInputValue("");
    }
  }, [date]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // If user is deleting, allow it
    if (value.length < inputValue.length) {
      setInputValue(value);
      if (value === "") {
        onDateChange(undefined);
      }
      return;
    }
    
    // Apply mask
    const formatted = formatDateInput(value);
    setInputValue(formatted);

    // Try to parse the date as DD/MM/YYYY when complete
    if (formatted.length === 10) {
      const parsedDate = parse(formatted, "dd/MM/yyyy", new Date());
      if (isValid(parsedDate)) {
        onDateChange(parsedDate);
      }
    } else if (formatted === "") {
      onDateChange(undefined);
    }
  };

  const handleInputBlur = () => {
    // Validate and reformat on blur
    if (inputValue && inputValue.length === 10) {
      const parsedDate = parse(inputValue, "dd/MM/yyyy", new Date());
      if (isValid(parsedDate)) {
        setInputValue(format(parsedDate, "dd/MM/yyyy"));
        onDateChange(parsedDate);
      } else {
        // Invalid date, clear it
        setInputValue("");
        onDateChange(undefined);
      }
    } else if (inputValue && inputValue.length > 0 && inputValue.length < 10) {
      // Incomplete date, clear it
      setInputValue("");
      onDateChange(undefined);
    }
  };

  const handleCalendarSelect = (selectedDate: Date | undefined) => {
    onDateChange(selectedDate);
    if (selectedDate && isValid(selectedDate)) {
      setInputValue(format(selectedDate, "dd/MM/yyyy"));
    } else {
      setInputValue("");
    }
    setOpen(false);
  };

  return (
    <div className="flex gap-2">
      <Input
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        className={cn("flex-1", className)}
        maxLength={10}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            disabled={disabled || readOnly}
            className={cn(
              "shrink-0 shadow-none",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleCalendarSelect}
            autoFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
